// AddEmployee.js
import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import "./AddEmployee.css";
import { useTheme } from "../../../context/ThemeContext";
import { BsPersonFill, BsBriefcaseFill, BsLockFill, BsCheck2, BsEye, BsEyeSlash } from "react-icons/bs";
import { Snackbar, Alert } from "@mui/material";

const AddEmployee = ({ addEmployeeToggle }) => {

  const [Name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [presentAddress, setPresentAddress] = useState("");
  const [address, setAddress] = useState("");
  const [bankD, setBankD] = useState("");
  const [pos, setpos] = useState("");
  const [cnic, setCNIC] = useState("");
  const [cardId, setCardId] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [dob, setDOB] = useState("");
  const [salary, setSalary] = useState("");
  const [pass, setPass] = useState("");
  const [required, setrequired] = useState(false);
  const [savedSuccessful, setsavedSuccessful] = useState(false);
  const [createdEmails, setcreatedEmails] = useState([]);
  const [alreadyCreatedEmp, setalreadyCreatedEmp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [department, setDepartment] = useState("");
  const [manager, setManager] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [allManagers, setAllManagers] = useState("");
  const [emailStatus, setEmailStatus] = useState(null); // null (sending) | "sent" | "failed"
  const [saveError, setSaveError] = useState("");        // visible server-side failure

  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [addEmpStep, setAddEmpStep] = useState(0);
  const [addEmpErrors, setAddEmpErrors] = useState({});
  const [showPass, setShowPass] = useState(false);

  const sendEmail = () => {
    const templateParams = {
      to_name: Name,
      to_email: email,
      pass: pass,
      from_name: "HR",
      from_email: "hr@growthguild.us",
    };

    // Return the promise so the caller can await it and surface success/failure to HR.
    return emailjs.send(
      "service_3p7klz8",
      "template_97o71qy",
      templateParams,
      "e8b24L9zqgYrt6tz9"
    );
  };

  const handleSend = async () => {
    setIsProcessing(true);
    setrequired(false);
    setalreadyCreatedEmp(false);
    setsavedSuccessful(false);
    setShowAlert(false);
    setSaveError("");
    setEmailStatus(null);
    const role = department === "Executive" ? "Executive" : "Employee";

    const eJoining = joiningDate ? new Date(joiningDate) : new Date();
    const eDOB = dob ? new Date(dob) : new Date();

    if (Name === "" || email === "" || pass === "") {
      setrequired(true);
      setIsProcessing(false);
      return;
    }

    try {
      // The backend is now the source of truth for duplicates: it returns
      // "created" for a new hire, "repaired" for a half-created one it healed,
      // or 409 "exists" when the employee is already fully set up.
      const response = await fetch("https://hr-backend-72v1.onrender.com/employee", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: Name,
          email: email,
          phone: phone,
          emergencyPhone: emergencyPhone,
          address: address,
          presentAddress: presentAddress,
          bankD: bankD,
          joining: eJoining,
          dob: eDOB,
          department: department,
          leadManager: manager.name,
          designation: role,
          salary: salary,
          pass: pass,
          pos: pos,
          cnic: cnic,
          cardId: cardId,
        }),
      });

      let data = {};
      try { data = await response.json(); } catch (_) { /* ignore parse errors */ }

      if (response.status === 409 || data.status === "exists") {
        setalreadyCreatedEmp(true);
        setIsProcessing(false);
        return;
      }
      if (!response.ok) {
        setSaveError(
          data.details || data.error ||
          "Something went wrong saving this employee. Please try again."
        );
        setIsProcessing(false);
        return;
      }

      // Saved (created or repaired). Assign to the chosen manager — best-effort:
      // a failure here must not block confirming the employee or emailing them.
      try {
        await fetch("https://hr-backend-72v1.onrender.com/assign-employee", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            managerEmail: manager.email,
            employeeName: Name,
            employeeEmail: email,
          }),
        });
      } catch (assignErr) {
        console.error("Manager assignment failed (non-blocking):", assignErr);
      }

      setsavedSuccessful(true);
      setShowAlert(true);

      // Email the credentials and SHOW HR the result — no more silent failures.
      try {
        await sendEmail();
        setEmailStatus("sent");
      } catch (mailErr) {
        console.error("Credentials email failed:", mailErr);
        setEmailStatus("failed");
      }
    } catch (error) {
      console.error("Error:", error);
      setSaveError("Couldn't reach the server. Check your connection and try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const response = await fetch("https://hr-backend-72v1.onrender.com/employees/emails");
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setcreatedEmails(data);
      } catch (error) {
        console.error("Error fetching emails:");
      }
    };
    fetchEmails();
  }, [savedSuccessful]);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const response = await fetch("https://hr-backend-72v1.onrender.com/employees-with-manager");
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setAllManagers(data);
      } catch (error) {
        console.error("Error fetching managers:");
      }
    };
    fetchManagers();
  }, []);

  const validateStep = (step) => {
    const errs = {};
    if (step === 0 && !Name.trim()) errs.name = "Full Name is required";
    if (step === 2) {
      if (!email.trim()) errs.email = "Email is required";
      if (!pass.trim()) errs.pass = "Password is required";
    }
    setAddEmpErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => { if (validateStep(addEmpStep)) setAddEmpStep(s => s + 1); };
  const handlePrev = () => setAddEmpStep(s => s - 1);

  return (
    <div className="addEmpPage">
      <div className={`addEmpPageCard stepperModal--${isDark ? "dark" : "light"}`}>

        {/* Header */}
        <div className="addEmpPageHeader">
          <h2 className="addEmpPageTitle">Add Employee</h2>
        </div>

        {/* Stepper bar */}
        <div className="stepperBar">
          {["Basic Info", "Job Details", "Account & Bank"].map((label, i) => (
            <React.Fragment key={i}>
              <div className="stepperStep">
                <div className={`stepperCircle ${addEmpStep > i ? "stepperCircle--done" : addEmpStep === i ? "stepperCircle--active" : "stepperCircle--inactive"}`}>
                  {addEmpStep > i ? <BsCheck2 /> : i + 1}
                </div>
                <span className={`stepperLabel ${addEmpStep === i ? "stepperLabel--active" : ""}`}>{label}</span>
              </div>
              {i < 2 && <div className={`stepperConnector ${addEmpStep > i ? "stepperConnector--done" : ""}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <div className="stepperContent">
          {savedSuccessful ? (
            <div className="stepperSuccess">
              <div className="stepperSuccessIcon"><BsCheck2 /></div>
              <h3>Employee Added Successfully!</h3>
              {emailStatus === "sent" && <p>Credentials emailed to {email}</p>}
              {emailStatus === null && <p>Sending credentials to {email}…</p>}
              {emailStatus === "failed" && (
                <p style={{ color: "#b26a00" }}>
                  Employee added, but the credentials email didn't send. Share their
                  login manually — Email: <b>{email}</b>, Password: <b>{pass}</b>.
                </p>
              )}
              <Snackbar
                open={showAlert}
                autoHideDuration={4000}
                onClose={() => { setShowAlert(false); if (emailStatus !== "failed") window.location.reload(); }}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <Alert onClose={() => setShowAlert(false)} severity={emailStatus === "failed" ? "warning" : "success"}>
                  {emailStatus === "failed"
                    ? "Employee saved — but the credentials email failed to send"
                    : "Employee Data Saved Successfully"}
                </Alert>
              </Snackbar>
            </div>
          ) : (
            <>
              {/* Step 0 - Basic Info */}
              {addEmpStep === 0 && (
                <div>
                  <div className="stepSectionHeading">
                    <BsPersonFill className="stepSectionIcon" />
                    <span>Basic Information</span>
                  </div>
                  <div className="stepGrid">
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${Name ? "stepInput--filled" : ""} ${addEmpErrors.name ? "stepInput--error" : ""}`}
                          value={Name}
                          onChange={e => setName(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Full Name *</label>
                      </div>
                      {addEmpErrors.name && <span className="stepFieldError">{addEmpErrors.name}</span>}
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${cnic ? "stepInput--filled" : ""}`}
                          value={cnic}
                          onChange={e => setCNIC(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">CNIC</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${cardId ? "stepInput--filled" : ""}`}
                          value={cardId}
                          onChange={e => setCardId(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Insurance Card Number</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${phone ? "stepInput--filled" : ""}`}
                          value={phone}
                          onChange={e => setPhone(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Phone</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${emergencyPhone ? "stepInput--filled" : ""}`}
                          value={emergencyPhone}
                          onChange={e => setEmergencyPhone(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Emergency Phone</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${address ? "stepInput--filled" : ""}`}
                          value={address}
                          onChange={e => setAddress(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Permanent Address</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${presentAddress ? "stepInput--filled" : ""}`}
                          value={presentAddress}
                          onChange={e => setPresentAddress(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Present Address</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap stepFieldWrap--full">
                      <div className="stepFieldGroup">
                        <input
                          type="date"
                          className={`stepInput stepInput--date ${dob ? "stepInput--filled" : ""}`}
                          value={dob}
                          onChange={e => setDOB(e.target.value)}
                        />
                        <label className="stepInputLabel stepInputLabel--alwaysShrunk">Date of Birth</label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1 - Job Details */}
              {addEmpStep === 1 && (
                <div>
                  <div className="stepSectionHeading">
                    <BsBriefcaseFill className="stepSectionIcon" />
                    <span>Job Details</span>
                  </div>
                  <div className="stepGrid">
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <select
                          className={`stepInput stepInput--select ${department ? "stepInput--filled" : ""}`}
                          value={department}
                          onChange={e => setDepartment(e.target.value)}
                        >
                          <option value=""></option>
                          {["Marketing Team", "Sales Team", "Content Team", "Dev Team", "Media Buying", "Finance", "HR", "Executive"].map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <label className="stepInputLabel stepInputLabel--alwaysShrunk">Department</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <select
                          className={`stepInput stepInput--select ${manager ? "stepInput--filled" : ""}`}
                          value={manager ? JSON.stringify({ name: manager.name, email: manager.email }) : ""}
                          onChange={e => e.target.value && setManager(JSON.parse(e.target.value))}
                        >
                          <option value=""></option>
                          {Array.isArray(allManagers) && allManagers.map(m => (
                            <option key={m.email} value={JSON.stringify({ name: m.name, email: m.email })}>
                              {m.name} ({m.email})
                            </option>
                          ))}
                        </select>
                        <label className="stepInputLabel stepInputLabel--alwaysShrunk">Manager</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${pos ? "stepInput--filled" : ""}`}
                          value={pos}
                          onChange={e => setpos(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Position</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${salary ? "stepInput--filled" : ""}`}
                          value={salary}
                          onChange={e => setSalary(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Salary</label>
                      </div>
                    </div>
                    <div className="stepFieldWrap stepFieldWrap--full">
                      <div className="stepFieldGroup">
                        <input
                          type="date"
                          className={`stepInput stepInput--date ${joiningDate ? "stepInput--filled" : ""}`}
                          value={joiningDate}
                          onChange={e => setJoiningDate(e.target.value)}
                        />
                        <label className="stepInputLabel stepInputLabel--alwaysShrunk">Joining Date</label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 - Account & Bank */}
              {addEmpStep === 2 && (
                <div>
                  <div className="stepSectionHeading">
                    <BsLockFill className="stepSectionIcon" />
                    <span>Account & Bank</span>
                  </div>
                  <div className="stepGrid">
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${email ? "stepInput--filled" : ""} ${addEmpErrors.email ? "stepInput--error" : ""}`}
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Email *</label>
                      </div>
                      {addEmpErrors.email && <span className="stepFieldError">{addEmpErrors.email}</span>}
                    </div>
                    <div className="stepFieldWrap">
                      <div className="stepFieldGroup stepFieldGroup--pass">
                        <input
                          type={showPass ? "text" : "password"}
                          className={`stepInput stepInput--pass ${pass ? "stepInput--filled" : ""} ${addEmpErrors.pass ? "stepInput--error" : ""}`}
                          value={pass}
                          onChange={e => setPass(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Login Password *</label>
                        <button type="button" className="passToggleBtn" onClick={() => setShowPass(p => !p)}>
                          {showPass ? <BsEyeSlash /> : <BsEye />}
                        </button>
                      </div>
                      {addEmpErrors.pass && <span className="stepFieldError">{addEmpErrors.pass}</span>}
                    </div>
                    <div className="stepFieldWrap stepFieldWrap--full">
                      <div className="stepFieldGroup">
                        <input
                          className={`stepInput ${bankD ? "stepInput--filled" : ""}`}
                          value={bankD}
                          onChange={e => setBankD(e.target.value)}
                          placeholder=" "
                        />
                        <label className="stepInputLabel">Bank Details</label>
                      </div>
                    </div>
                    {alreadyCreatedEmp && (
                      <div className="stepFieldWrap stepFieldWrap--full">
                        <span className="stepFieldError">An employee with this email already exists.</span>
                      </div>
                    )}
                    {saveError && (
                      <div className="stepFieldWrap stepFieldWrap--full">
                        <span className="stepFieldError">{saveError}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer navigation */}
        {!savedSuccessful && (
          <div className="stepperFooter" style={{ justifyContent: addEmpStep > 0 ? "space-between" : "flex-end" }}>
            {addEmpStep > 0 && (
              <button className="stepperNavBtn stepperNavBtn--prev" onClick={handlePrev}>Previous</button>
            )}
            {addEmpStep < 2 ? (
              <button className="stepperNavBtn stepperNavBtn--next" onClick={handleNext}>Next</button>
            ) : (
              <button className="stepperNavBtn stepperNavBtn--next" onClick={handleSend} disabled={isProcessing}>
                {isProcessing ? "Adding..." : "Add Employee"}
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AddEmployee;
